import { Injectable, inject } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { LoyaltyService } from '@src/api';
import { map, startWith, switchMap, tap } from 'rxjs/operators';
import { TUI_DEFAULT_MATCHER } from '@taiga-ui/cdk';
import { Nullable } from '@src/types/utils';

import { GroupItem } from '../types';

import { convertToCategories } from './utils/convertToCategories';
import { CategoriesStoreKeys, LpCategoriesStoreService } from './store';

@Injectable({
  providedIn: 'root',
})
export class LoyaltyProgramFilterService {
  private readonly api = inject(LoyaltyService);
  private readonly lpCategoriesStore = inject(LpCategoriesStoreService);

  categoriesInUse = false;

  readonly categoriesForUnion$ = new BehaviorSubject<Nullable<ReadonlyArray<GroupItem>>>([]);

  private readonly searchCategoriesForUnion$ = new BehaviorSubject<string>('');

  readonly filteredCategoriesForUnion$ = this.searchCategoriesForUnion$.pipe(
    startWith(''),
    switchMap(search =>
      this.categoriesForUnion$.pipe(
        map(items => {
          if (items === null) {
            return items;
          }

          return items.reduce<GroupItem[]>((acc, item) => {
            const items = item.items.filter(({ name }) => TUI_DEFAULT_MATCHER(name, search));
            if (items.length) {
              acc.push({
                ...item,
                items,
              });
            }

            return acc;
          }, []);
        }),
      ),
    ),
  );

  getCategories(inUse: boolean, force?: boolean) {
    if (this.lpCategoriesStore.getData(CategoriesStoreKeys.InUse)?.length && inUse === this.categoriesInUse && !force) {
      // Если данные уже были загружены, то запрос не делаем
      return;
    }

    this.api
      .categoryGroups(inUse)
      .pipe(
        map(result => convertToCategories(result ?? [])),
        tap(categories => {
          if (inUse) {
            this.lpCategoriesStore.setData(CategoriesStoreKeys.InUse, categories);
          } else {
            this.lpCategoriesStore.setData(CategoriesStoreKeys.All, categories);
          }
        }),
      )
      .subscribe(() => {
        this.categoriesInUse = inUse;
      });
  }

  getCategoriesForUnion(inUse: boolean) {
    this.api.categoryGroups(inUse).subscribe(result => {
      this.categoriesForUnion$.next(convertToCategories(result ?? []));
    });
  }

  searchCategoriesForUnion(query: Nullable<string>) {
    this.searchByQuery(query, this.searchCategoriesForUnion$);
  }

  private searchByQuery(query: Nullable<string>, stream: BehaviorSubject<string>) {
    const length = query?.length ?? 0;
    if (length > 2) {
      stream.next(query ?? '');
    } else if (stream.value !== '') {
      stream.next('');
    }
  }
}
